/**
 * roomStore - this tab's view of shared room state: breakout, poll, Q&A,
 * speaking set, lobby (waiting room), follow-me, room lock, roster.
 *
 * ONE WRITER PER SLICE: the owners under src/multiplayer/ write breakout,
 * poll, Q&A, lobby, follow, lock, and roster state. speakingDetector owns
 * `speaking`, and the current PollZoneManager scene owns locally derived
 * `pollCounts`.
 * Everything here is a PROJECTION of authoritative remote state (Photon room
 * properties / server responses); UI reads it, UI never writes it. Writes go
 * to Photon/the API and come back through the writer - never patch this
 * store optimistically from a component.
 *
 * Late-subscriber hydration - the whole reason this store exists - is free:
 * `roomStore.getState()` at mount time replaces the getState()/getQueue()
 * priming calls each panel hand-rolls today.
 */
import { createAppStore } from './createAppStore';
import type { AnyBreakoutState } from '../utils/breakoutProtocol';
import type { AnyPollState } from '../utils/pollProtocol';
import type { QueueEntry, QaState } from '../utils/handQueueProtocol';

/** One remote participant as harvested by multiplayer/roster.ts. */
export interface RosterEntry {
  actorNr: number;
  /** true only on the People panel's synthesized local-player row */
  isSelf?: boolean;
  name: string;
  role?: string;
  /** false = they are in another level (distance is null there) */
  sameLevel: boolean;
  level: string | null;
  /** which breakout room they're in, if any (null in the main hall) */
  breakoutRoomId: string | null;
  distance: number | null;
  /** avatar swatch color (People panel row dot) */
  color?: string;
  /** synced media state from Photon event 52; undefined until first report */
  mediaState?: { mic?: boolean; camera?: boolean };
  agoraUid?: string | number | null;
  /** Agora network quality score (1-2 good, 3-4 fair, 5-6 poor; 0/null unknown) */
  networkQuality?: number | null;
}

/** One guest waiting in the lobby (server shape from getPendingEntries). */
export interface LobbyEntry {
  id: string;
  displayName?: string;
}

/** Derived Q&A projection consumed by the HUD, panel, and scene markers. */
export interface QaSnapshot {
  queue: QueueEntry[];
  floor: QaState | null;
  myActorNr: number | null;
  myPosition: number;
}

export interface PollCounts {
  pollId: string;
  counts: Record<string, number>;
  myZone: string | null;
  total: number;
}

export interface RoomState {
  /** null = no state received yet (pre-join) */
  breakout: AnyBreakoutState | null;
  poll: AnyPollState | null;
  pollCounts: PollCounts | null;
  qa: QaSnapshot | null;
  speaking: { uids: ReadonlySet<string>; local: boolean };
  lobby: { enabled: boolean; pending: LobbyEntry[] };
  followMeActive: boolean;
  roomLocked: boolean;
  roster: { entries: RosterEntry[]; currentLevel: string | null; updatedAt: number };
}

function initialRoomState(): RoomState {
  return {
    breakout: null,
    poll: null,
    pollCounts: null,
    qa: null,
    speaking: { uids: new Set(), local: false },
    lobby: { enabled: true, pending: [] },
    followMeActive: false,
    roomLocked: false,
    roster: { entries: [], currentLevel: null, updatedAt: 0 },
  };
}

export const roomStore = createAppStore<RoomState>(initialRoomState());

export function setBreakout(state: AnyBreakoutState): void {
  roomStore.setState({ breakout: state });
}

export function setPoll(state: AnyPollState): void {
  roomStore.setState((current) => ({
    poll: state,
    pollCounts:
      current.pollCounts && state.active && current.pollCounts.pollId === state.pollId
        ? current.pollCounts
        : null,
  }));
}

export function setPollCounts(counts: PollCounts | null): void {
  const poll = roomStore.getState().poll;
  if (counts && (!poll?.active || poll.pollId !== counts.pollId)) return;
  roomStore.setState({
    pollCounts: counts
      ? { ...counts, counts: { ...counts.counts }, myZone: counts.myZone ?? null }
      : null,
  });
}

export function setQa(snapshot: QaSnapshot): void {
  roomStore.setState({ qa: snapshot });
}

export function setSpeaking(uids: Iterable<string>, local: boolean): void {
  roomStore.setState({ speaking: { uids: new Set(uids), local } });
}

export function setLobby(enabled: boolean, pending?: LobbyEntry[]): void {
  const prev = roomStore.getState().lobby;
  roomStore.setState({ lobby: { enabled, pending: pending ?? prev.pending } });
}

export function setFollowMeActive(active: boolean): void {
  roomStore.setState({ followMeActive: active });
}

export function setRoomLocked(locked: boolean): void {
  roomStore.setState({ roomLocked: locked });
}

export function setRoster(
  entries: RosterEntry[],
  currentLevel: string | null,
  updatedAt: number,
): void {
  roomStore.setState({ roster: { entries, currentLevel, updatedAt } });
}

/** Reconnect/different-room/terminal reset. Related projections change once. */
export function resetRoomState(): void {
  roomStore.setState(initialRoomState(), true);
}

/** Scene-owned projections cannot outlive the scene that derived them. */
export function resetRoomSceneState(currentLevel: string | null = null): void {
  roomStore.setState({
    pollCounts: null,
    roster: { entries: [], currentLevel, updatedAt: Date.now() },
  });
}
